/**
 * Biometric to Morph Channel Mapper
 * 
 * Maps normalized biometric inputs to morph channel values using weighted influence.
 * This creates anatomically meaningful deformations by routing each biometric
 * to one or more morph channels with appropriate weights.
 */

import { getAllChannelIds } from './MorphChannelDefinitions';

/**
 * Mapping matrix defining how biometric inputs influence morph channels
 * 
 * Structure:
 * biometric_id: {
 *   morph_channel_id: weight (0-1),
 *   ...
 * }
 * 
 * Weights determine how much influence the biometric has on each channel.
 * Total weights per biometric don't need to sum to 1.
 */
export const BIOMETRIC_TO_MORPH_MAPPING = {
  // BPD primarily affects head width - INCREASED for visible changes
  bpd: {
    head_width: 0.9,
    cranial_volume: 0.3,
    forehead_width: 0.2,
    midface_width: 0.15
  },

  // HC affects overall cranial measurements
  hc: {
    cranial_volume: 0.6,
    head_height: 0.5,
    head_width: 0.2,
    head_length: 0.2
  },

  // OFD (Önarka baş) mainly affects head length/depth
  ofd: {
    head_length: 0.85,
    cranial_volume: 0.2,
    forehead_projection: 0.15
  },

  // Fromen affects forehead and overall face length - INCREASED
  fromen: {
    forehead_width: 0.7,
    forehead_projection: 0.6,
    face_length: 0.5,
    head_height: 0.2
  },

  // Burun affects nose morphology - INCREASED for visible nose tip
  burun: {
    nose_projection: 0.9,
    nose_vertical: 0.7
  },

  // Göztepe affects eye spacing and orbital region
  goztepe: {
    eye_spacing: 0.85,
    orbital_depth: 0.3,
    midface_width: 0.2
  },

  // BiocÇap affects midface width - INCREASED for flat/wide nose effect
  bioc_cap: {
    midface_width: 0.8,
    eye_spacing: 0.25,
    mouth_width: 0.2,
    nose_projection: -0.15 // Negative = flatter nose when bioc_cap increases
  },

  // Çene affects jaw and chin - INCREASED for visible chin
  cene: {
    chin_length: 0.9,
    jaw_projection: 0.6,
    face_length: 0.4
  },

  // Ağızçapı affects mouth width
  agiz_capi: {
    mouth_width: 0.55,
    upper_lip_fullness: 0.05
  },

  // Göz affects orbital region
  goz: {
    orbital_depth: 0.45,
    eye_spacing: 0.08
  },

  // Üst Dudak affects upper lip
  ust_dudak: {
    upper_lip_fullness: 0.55,
    mouth_width: 0.05,
    nose_vertical: 0.02
  }
};

/**
 * Corrective influence matrix
 * 
 * Defines automatic corrective adjustments when certain morphs are activated.
 * Prevents ugly deformations by adding subtle corrections.
 * 
 * Structure:
 * morph_channel_id: {
 *   triggered_morph_id: { threshold, weight }
 * }
 */
export const CORRECTIVE_INFLUENCES = {
  // When head is very wide, slightly increase forehead projection
  head_width: {
    forehead_projection: { threshold: 0.3, weight: 0.15 }
  },
  
  // When face is long, slightly increase chin projection
  face_length: {
    jaw_projection: { threshold: 0.2, weight: 0.2 },
    chin_length: { threshold: 0.25, weight: 0.1 }
  },
  
  // When nose projects a lot, slightly adjust mouth area
  nose_projection: {
    upper_lip_fullness: { threshold: 0.3, weight: 0.1 }
  },
  
  // When eyes are far apart, slightly widen midface
  eye_spacing: {
    midface_width: { threshold: 0.25, weight: 0.15 }
  },
  
  // When chin is long, adjust jaw projection
  chin_length: {
    jaw_projection: { threshold: 0.3, weight: 0.2 }
  }
};

/**
 * BiometricToMorphMapper class
 * 
 * Converts normalized biometric values to morph channel values.
 */
export class BiometricToMorphMapper {
  constructor() {
    this.mapping = BIOMETRIC_TO_MORPH_MAPPING;
    this.correctives = CORRECTIVE_INFLUENCES;
    this.channelIds = getAllChannelIds();
  }

  /**
   * Map normalized biometrics to morph channel values
   * 
   * @param {Object} normalizedBiometrics - Normalized biometric values (-1 to 1)
   * @returns {Object} Morph channel values
   */
  mapToMorphValues(normalizedBiometrics) {
    // Initialize all channels to 0
    const morphValues = {};
    for (const channelId of this.channelIds) {
      morphValues[channelId] = 0;
    }

    // First pass: direct mapping
    for (const [biometricId, channelWeights] of Object.entries(this.mapping)) {
      const biometricValue = normalizedBiometrics[biometricId] ?? 0;
      
      for (const [channelId, weight] of Object.entries(channelWeights)) {
        if (morphValues.hasOwnProperty(channelId)) {
          // Accumulate weighted contributions
          morphValues[channelId] += biometricValue * weight;
        }
      }
    }

    // Second pass: apply corrective influences
    this.applyCorrectiveInfluences(morphValues);

    // Third pass: clamp and smooth values
    for (const channelId of this.channelIds) {
      morphValues[channelId] = this.clampAndSmooth(morphValues[channelId]);
    }

    return morphValues;
  }

  /**
   * Apply corrective influences to prevent ugly deformations
   */
  applyCorrectiveInfluences(morphValues) {
    for (const [sourceChannel, corrections] of Object.entries(this.correctives)) {
      const sourceValue = Math.abs(morphValues[sourceChannel] ?? 0);
      
      for (const [targetChannel, config] of Object.entries(corrections)) {
        if (sourceValue > config.threshold) {
          const correctionAmount = (sourceValue - config.threshold) * config.weight;
          const direction = Math.sign(morphValues[sourceChannel] || 1);
          
          if (morphValues.hasOwnProperty(targetChannel)) {
            morphValues[targetChannel] += correctionAmount * direction;
          }
        }
      }
    }
  }

  /**
   * Clamp value and apply smoothing curve
   * 
   * Uses smoothstep-like function to prevent harsh jumps at extremes
   */
  clampAndSmooth(value) {
    // Clamp to reasonable range
    const clamped = Math.max(-1, Math.min(1, value));
    
    // Apply smoothstep for more natural feel at extremes
    // This reduces the rate of change near -1 and 1
    const t = (clamped + 1) / 2; // Convert to 0-1
    const smoothed = t * t * (3 - 2 * t); // Smoothstep
    return smoothed * 2 - 1; // Back to -1 to 1
  }

  /**
   * Get the contribution breakdown for a specific morph channel
   * 
   * Useful for debugging which biometrics affect which channels
   */
  getChannelContributors(channelId) {
    const contributors = [];
    
    for (const [biometricId, channelWeights] of Object.entries(this.mapping)) {
      if (channelWeights[channelId]) {
        contributors.push({
          biometric: biometricId,
          weight: channelWeights[channelId]
        });
      }
    }
    
    return contributors.sort((a, b) => b.weight - a.weight);
  }

  /**
   * Get all channels affected by a specific biometric
   */
  getAffectedChannels(biometricId) {
    return this.mapping[biometricId] ? Object.keys(this.mapping[biometricId]) : [];
  }
}

// Export singleton instance
export const biometricToMorphMapper = new BiometricToMorphMapper();

export default BiometricToMorphMapper;
